import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableBody,
  TableHead,
  TableRow,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';

export interface CN56ColumnGroup {
  id: string;
  Header: string;
  columns: CN56Column[];
}

export interface CN56Column {
  accessor: string;
  Header: string | React.ReactNode;
  width?: string;
  textAlign?: 'left' | 'center' | 'right';
  Cell?: ({ value, row }: { value: any; row: any }) => React.ReactNode;
}

export interface CN56TableProps<T extends object> {
  data: T[];
  columnGroups?: CN56ColumnGroup[];
  columns?: CN56Column[];
  moduleName?: string;
  viewMode?: boolean;
  disableAutoSizing?: boolean;
  pageSize?: number;
  lazyLoad?: boolean;
  lazyLoadHeight?: number;
  lazyLoadingBatchSize?: number;
  isLoading?: boolean;
  rowClassName?: (row: T) => string;
  rowBackgroundColor?: string | ((row: T) => string);
  summaryRows?: {
    subTotalRow?: any;
    weightProportionsAndIpkRow?: any;
    cn56SummaryRows?: {
      sampledFlows?: any;
      nonSampledFlows?: any;
      allFlows?: any;
    };
  };
}

// Styled components similar to GroupedTable
const StyledTableContainer = styled('div')(() => ({
  maxHeight: '70vh',
  overflowX: 'auto',
  overflowY: 'auto',
  border: '1px solid #E1E8ED',
  borderRadius: '8px',
}));

const StyledPaper = styled('div')(() => ({
  width: '100%',
  overflow: 'hidden',
  backgroundColor: '#FFFFFF',
}));

const StyledTable = styled('table')(() => ({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '1px',
  backgroundColor: '#F5F5F5',
}));

const TableWrapper = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const TableTitle = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#063142',
  marginBottom: '8px',
}));

// Header cells similar to GroupedTable
const HeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 700,
  fontSize: '12px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: theme.spacing(1, 1.5),
  position: 'sticky',
  zIndex: 1300,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  border: 'none',
}));

const SubHeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 600,
  fontSize: '11px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: theme.spacing(0.75, 1.5),
  position: 'sticky',
  zIndex: 1298,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  border: 'none',
}));

const DataCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#FFFFFF',
  border: 'none',
  padding: theme.spacing(0.75, 1.5),
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 500,
  },
}));

const SummaryCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#F8F9FA',
  border: 'none',
  padding: theme.spacing(0.75, 1.5),
  fontWeight: 600,
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 600,
  },
  '&.subtotal-row': {
    backgroundColor: '#E3F2FD',
  },
  '&.proportion-row': {
    backgroundColor: '#FFF3E0',
    borderTop: '1px solid #FF9800',
  },
}));

// Helper function to calculate text width
function calculateTextWidth(text: string, font: string): number {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = font;
    return context.measureText(text).width;
  }
  return text.length * 8; // Fallback
}

// Debounce function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * CN56Table component with column grouping support
 * 
 * This component is specifically designed for CN56 Report tables,
 * supporting both grouped and flat column layouts depending on the data structure.
 * Based on the GroupedTable component but adapted for CN56 specific needs.
 */
function CN56Table<T extends object>({
  data = [],
  columnGroups = [],
  columns = [],
  moduleName,
  viewMode = false,
  disableAutoSizing = false,
  pageSize = 25,
  lazyLoad = false,
  lazyLoadHeight = 400,
  lazyLoadingBatchSize = 25,
  isLoading = false,
  rowClassName,
  rowBackgroundColor,
  summaryRows,
  ...props
}: CN56TableProps<T>): React.ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [isCalculating, setIsCalculating] = useState(true);

  // Determine if we're using grouped or flat columns
  const isGroupedTable = columnGroups.length > 0;
  
  // Flatten all columns from groups or use direct columns
  const allColumns = useMemo(() => {
    if (isGroupedTable) {
      return columnGroups.reduce<CN56Column[]>((acc, group) => {
        return [...acc, ...group.columns];
      }, []);
    }
    return columns;
  }, [columnGroups, columns, isGroupedTable]);

  // Calculate column widths based on content
  const calculateColumnWidths = useCallback(() => {
    if (!tableContainerRef.current || data.length === 0 || disableAutoSizing) {
      setIsCalculating(false);
      return;
    }

    const newColumnWidths: Record<string, number> = {};
    const headerFont = '700 12px "Lato", sans-serif';
    const cellFont = '400 12px "Lato", sans-serif';

    // Define min and max column widths
    const minColWidth = isMobile ? 60 : 80;
    const maxColWidth = isMobile ? 120 : 200;

    // Calculate width for each column
    allColumns.forEach(column => {
      // Start with header width - handle both string and React.ReactNode
      const headerText = typeof column.Header === 'string' ? column.Header : String(column.accessor);
      let maxWidth = Math.max(calculateTextWidth(headerText, headerFont) + 16, minColWidth);

      // Sample data to get better width estimates
      data.slice(0, 10).forEach(row => {
        const value = (row as any)[column.accessor];
        let displayValue = '';
        
        if (column.Cell) {
          // For custom cells, use a rough estimate
          displayValue = String(value || '').substring(0, 15);
        } else if (typeof value === 'number') {
          // Format numbers properly for CN56
          displayValue = formatNumber(value, column.accessor);
        } else {
          displayValue = String(value || '');
        }
        
        const contentWidth = calculateTextWidth(displayValue, cellFont) + 16;
        maxWidth = Math.max(maxWidth, contentWidth);
      });

      // Apply min/max constraints
      newColumnWidths[column.accessor] = Math.min(Math.max(maxWidth, minColWidth), maxColWidth);
    });

    // Convert numeric widths to strings with 'px' suffix
    const finalColumnWidths: Record<string, string> = {};
    for (const [key, value] of Object.entries(newColumnWidths)) {
      finalColumnWidths[key] = `${value}px`;
    }

    setColumnWidths(finalColumnWidths);
    setIsCalculating(false);
  }, [data, allColumns, disableAutoSizing, isMobile]);

  // Debounced version of the calculation function
  const debouncedCalculateColumnWidths = debounce(calculateColumnWidths, 300);

  // Calculate column widths on initial render and when data changes
  useEffect(() => {
    setIsCalculating(true);
    calculateColumnWidths();
  }, [data, columnGroups, columns]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsCalculating(true);
      debouncedCalculateColumnWidths();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [debouncedCalculateColumnWidths]);

  // Helper function to format numbers for CN56
  const formatNumber = (value: any, columnAccessor?: string): string => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    
    // CN56 specific number formatting
    if (columnAccessor?.toLowerCase().includes('grosssum') || 
        columnAccessor?.toLowerCase().includes('netkg') ||
        columnAccessor?.toLowerCase().includes('pkg') ||
        columnAccessor?.toLowerCase().includes('gkg') ||
        columnAccessor?.toLowerCase().includes('ekg') ||
        columnAccessor?.toLowerCase().includes('jkg') ||
        columnAccessor?.toLowerCase().includes('zkg')) {
      return num.toFixed(2);
    }
    
    if (columnAccessor?.toLowerCase().includes('effectivesamplingrate') ||
        columnAccessor?.toLowerCase().includes('netipk')) {
      return num.toFixed(4);
    }
    
    // Items/count fields - show as integers
    if (columnAccessor?.toLowerCase().includes('items') || 
        columnAccessor?.toLowerCase().includes('samples') ||
        columnAccessor?.toLowerCase().includes('taresum')) {
      return Math.round(num).toString();
    }
    
    // For other numbers - show as integers if they are whole numbers, otherwise with decimals
    if (num % 1 === 0) {
      return Math.round(num).toString();
    }
    
    return num.toString();
  };

  // Render cell content
  const renderCell = (row: T, column: CN56Column) => {
    const value = (row as any)[column.accessor];
    
    if (column.Cell) {
      return column.Cell({ value, row });
    }
    
    // Check for string version of the field first (e.g., totalPGrossStr instead of totalPGross)
    const stringFieldName = `${column.accessor}Str`;
    const stringValue = (row as any)[stringFieldName];
    
    if (stringValue !== undefined && stringValue !== null && stringValue !== '') {
      return String(stringValue);
    }
    
    // Fallback to numeric value with proper formatting
    if (typeof value === 'number') {
      return formatNumber(value, column.accessor);
    }
    
    return String(value || '');
  };

  // Function to determine if a column contains numeric data
  const isNumericColumn = (accessor: string): boolean => {
    return accessor.toLowerCase().includes('sum') || 
           accessor.toLowerCase().includes('kg') || 
           accessor.toLowerCase().includes('items') || 
           accessor.toLowerCase().includes('samples') ||
           accessor.toLowerCase().includes('rate') ||
           accessor.toLowerCase().includes('ipk');
  };

  // Show loading state
  if (isLoading || isCalculating) {
    return (
      <TableWrapper data-testid="cn56-table-loading">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="cn56-table-loading-content">
          <CircularProgress size={40} data-testid="cn56-table-loading-spinner" />
          <Typography sx={{ mt: 2, color: '#666' }} data-testid="cn56-table-loading-text">
            {isCalculating ? 'Calculating layout...' : 'Loading data...'}
          </Typography>
        </Box>
      </TableWrapper>
    );
  }

  // Show empty state
  if (data.length === 0) {
    return (
      <TableWrapper data-testid="cn56-table-empty">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="cn56-table-empty-content">
          <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="cn56-table-empty-text">
            No data available
          </Typography>
        </Box>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper data-testid="cn56-table-wrapper">
      <StyledPaper data-testid="cn56-table-paper">
        <StyledTableContainer ref={tableContainerRef} data-testid="cn56-table-container">
          <StyledTable data-testid="cn56-table">
            <TableHead data-testid="cn56-table-head">
              {isGroupedTable ? (
                <>
                  {/* Group Headers Row */}
                  <TableRow sx={{ 
                    '& th': { 
                      px: 1.5, 
                      py: 1,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1300,
                      backgroundColor: '#E7EFF5 !important',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    } 
                  }}>
                    {columnGroups.map(group => {
                      // Only render group header if it has content
                      if (!group.Header) {
                        // For groups without headers, render individual column headers with rowSpan=2
                        return group.columns.map(column => (
                          <HeaderCell
                            key={column.accessor}
                            rowSpan={2}
                            style={{ 
                              width: columnWidths[column.accessor],
                              textAlign: column.textAlign || 'center'
                            }}
                          >
                            {column.Header}
                          </HeaderCell>
                        ));
                      } else {
                        // For groups with headers, render group header normally
                        return (
                          <HeaderCell
                            key={group.id}
                            colSpan={group.columns.length}
                            style={{ textAlign: 'center' }}
                          >
                            {group.Header}
                          </HeaderCell>
                        );
                      }
                    })}
                  </TableRow>
                  
                  {/* Individual Column Headers Row - only for groups that have group headers */}
                  <TableRow sx={{ 
                    '& th': { 
                      px: 1.5, 
                      py: 0.75,
                      position: 'sticky',
                      top: '24px',
                      zIndex: 1299,
                      backgroundColor: '#E7EFF5 !important',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    } 
                  }}>
                    {columnGroups.map(group => {
                      // Only render column headers for groups that have group headers
                      if (group.Header) {
                        return group.columns.map(column => (
                          <SubHeaderCell
                            key={column.accessor}
                            style={{ 
                              width: columnWidths[column.accessor],
                              textAlign: column.textAlign || 'center'
                            }}
                          >
                            {column.Header}
                          </SubHeaderCell>
                        ));
                      }
                      return null; // Skip columns that already have rowSpan=2 headers
                    })}
                  </TableRow>
                </>
              ) : (
                // Simple header row for flat columns
                <TableRow sx={{ 
                  '& th': { 
                    px: 1.5, 
                    py: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1300,
                    backgroundColor: '#E7EFF5 !important',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  } 
                }}>
                  {columns.map(column => (
                    <HeaderCell
                      key={column.accessor}
                      style={{ 
                        width: columnWidths[column.accessor],
                        textAlign: column.textAlign || 'center'
                      }}
                    >
                      {column.Header}
                    </HeaderCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            
            <TableBody data-testid="cn56-table-body">
              {data.map((row: any, index: number) => (
                <TableRow 
                  key={row.id || index} 
                  sx={{ '& td': { px: 1.5, py: 0.75 } }}
                  data-testid={`cn56-table-row-${index}`}
                >
                  {allColumns.map(column => {
                    const isNumeric = isNumericColumn(column.accessor);
                    
                    return (
                      <DataCell
                        key={column.accessor}
                        className={isNumeric ? 'number-cell' : ''}
                        style={{
                          backgroundColor: rowBackgroundColor 
                            ? typeof rowBackgroundColor === 'function' 
                              ? rowBackgroundColor(row)
                              : rowBackgroundColor
                            : '#FFFFFF',
                          textAlign: column.textAlign || 'center'
                        }}
                      >
                        {renderCell(row, column)}
                      </DataCell>
                    );
                  })}
                </TableRow>
              ))}
              
              {/* CN56 Summary Rows - Totals section */}
              {summaryRows?.cn56SummaryRows && (
                <>
                  {/* First Summary Row - Sampled flows */}
                  <TableRow sx={{ '& td': { px: 1.5, py: 0.75 } }} data-testid="cn56-table-summary-sampled-row">
                    {/* MC column - empty */}
                    <SummaryCell className="subtotal-row"></SummaryCell>
                    
                    {/* Totals - spans 3 rows */}
                    <SummaryCell
                      className="subtotal-row"
                      rowSpan={3}
                      style={{ 
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: '12px'
                      }}
                    >
                      Totals
                    </SummaryCell>
                    
                    {/* Sampled flows - spans 3 columns (OE To, MSC, Month) */}
                    <SummaryCell
                      className="subtotal-row"
                      colSpan={3}
                      style={{ 
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '11px'
                      }}
                    >
                      Sampled flows
                    </SummaryCell>
                    
                    {/* Data columns for sampled flows */}
                    {allColumns.slice(5).map(column => {
                      const isNumeric = isNumericColumn(column.accessor);
                      const value = summaryRows.cn56SummaryRows?.sampledFlows?.[column.accessor];
                      const stringValue = summaryRows.cn56SummaryRows?.sampledFlows?.[`${column.accessor}Str`];
                      
                      return (
                        <SummaryCell
                          key={`sampled-${column.accessor}`}
                          className={`subtotal-row ${isNumeric ? 'number-cell' : ''}`}
                          style={{ textAlign: column.textAlign || 'center' }}
                        >
                          {stringValue !== undefined && stringValue !== null && stringValue !== '' 
                            ? String(stringValue)
                            : value !== undefined 
                              ? (isNumeric ? formatNumber(value, column.accessor) : String(value)) 
                              : ''}
                        </SummaryCell>
                      );
                    })}
                  </TableRow>
                  
                  {/* Second Summary Row - Non-Sampled flows */}
                  <TableRow sx={{ '& td': { px: 1.5, py: 0.75 } }} data-testid="cn56-table-summary-non-sampled-row">
                    {/* MC column - empty */}
                    <SummaryCell className="subtotal-row"></SummaryCell>
                    
                    {/* Totals cell is rowSpan=3, so skip it here */}
                    
                    {/* Non-Sampled flows - spans 3 columns */}
                    <SummaryCell
                      className="subtotal-row"
                      colSpan={3}
                      style={{ 
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '11px'
                      }}
                    >
                      Non-Sampled flows
                    </SummaryCell>
                    
                    {/* Data columns for non-sampled flows */}
                    {allColumns.slice(5).map(column => {
                      const isNumeric = isNumericColumn(column.accessor);
                      const value = summaryRows.cn56SummaryRows?.nonSampledFlows?.[column.accessor];
                      const stringValue = summaryRows.cn56SummaryRows?.nonSampledFlows?.[`${column.accessor}Str`];
                      
                      return (
                        <SummaryCell
                          key={`non-sampled-${column.accessor}`}
                          className={`subtotal-row ${isNumeric ? 'number-cell' : ''}`}
                          style={{ textAlign: column.textAlign || 'center' }}
                        >
                          {stringValue !== undefined && stringValue !== null && stringValue !== '' 
                            ? String(stringValue)
                            : value !== undefined 
                              ? (isNumeric ? formatNumber(value, column.accessor) : String(value)) 
                              : ''}
                        </SummaryCell>
                      );
                    })}
                  </TableRow>
                  
                  {/* Third Summary Row - All flows */}
                  <TableRow sx={{ '& td': { px: 1.5, py: 0.75 } }} data-testid="cn56-table-summary-all-flows-row">
                    {/* MC column - empty */}
                    <SummaryCell className="subtotal-row"></SummaryCell>
                    
                    {/* Totals cell is rowSpan=3, so skip it here */}
                    
                    {/* All flows - spans 3 columns */}
                    <SummaryCell
                      className="subtotal-row"
                      colSpan={3}
                      style={{ 
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '11px'
                      }}
                    >
                      All flows
                    </SummaryCell>
                    
                    {/* Data columns for all flows */}
                    {allColumns.slice(5).map(column => {
                      const isNumeric = isNumericColumn(column.accessor);
                      const value = summaryRows.cn56SummaryRows?.allFlows?.[column.accessor];
                      const stringValue = summaryRows.cn56SummaryRows?.allFlows?.[`${column.accessor}Str`];
                      
                      return (
                        <SummaryCell
                          key={`all-${column.accessor}`}
                          className={`subtotal-row ${isNumeric ? 'number-cell' : ''}`}
                          style={{ textAlign: column.textAlign || 'center' }}
                        >
                          {stringValue !== undefined && stringValue !== null && stringValue !== '' 
                            ? String(stringValue)
                            : value !== undefined 
                              ? (isNumeric ? formatNumber(value, column.accessor) : String(value)) 
                              : ''}
                        </SummaryCell>
                      );
                    })}
                  </TableRow>
                </>
              )}
              
              {/* Other Summary Rows - if needed for CN56 */}
              {summaryRows?.subTotalRow && (
                <TableRow sx={{ '& td': { px: 1.5, py: 0.75 } }} data-testid="cn56-table-subtotal-row">
                  <SummaryCell
                    className="subtotal-row"
                    colSpan={Math.min(3, allColumns.length)}
                  >
                    Subtotals
                  </SummaryCell>
                  
                  {allColumns.slice(3).map(column => {
                    const isNumeric = isNumericColumn(column.accessor);
                    const value = summaryRows.subTotalRow[column.accessor];
                    const stringValue = summaryRows.subTotalRow[`${column.accessor}Str`];
                    
                    return (
                      <SummaryCell
                        key={column.accessor}
                        className={`subtotal-row ${isNumeric ? 'number-cell' : ''}`}
                        style={{ textAlign: column.textAlign || 'center' }}
                      >
                        {stringValue !== undefined && stringValue !== null && stringValue !== '' 
                          ? String(stringValue)
                          : value !== undefined 
                            ? (isNumeric ? formatNumber(value, column.accessor) : String(value)) 
                            : ''}
                      </SummaryCell>
                    );
                  })}
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </StyledPaper>
    </TableWrapper>
  );
}

export default CN56Table; 